import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TransactionLog } from 'src/app/services/logs/transaction-logs';
import { TOCA } from 'src/app/components/catalogs/beamline-catalog/resource/toca';
import { environment } from 'src/environments/environment';
import { BeamlineRequiringApproval } from './models/beamline-requiring-approval';
import { Action } from 'src/app/common/enumerations/enumerations';
import { Location, Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';


@Injectable({
  providedIn: 'root'
})
export class BratoiService {

  public api: string = environment.urls.baseApiUrl + '/BeamlineRequiringApproval';

  constructor(
    private http: HttpClient
  ) { }

  GetBeamlinelApprovals(): Observable<BeamlineRequiringApproval[]> {
    return this.http.get<BeamlineRequiringApproval[]>(this.api);
  }

  GetBeamlinelApproval(id: number): Observable<BeamlineRequiringApproval> {
    return this.http.get<BeamlineRequiringApproval>(this.api + '/' + id);
  }

  GetHistoryByBlRequiring(id: number): Observable<TransactionLog[]> {
    return this.http.get<TransactionLog[]>(this.api + '/History/' + id);
  }

  CreateBlApproval(blRequiringApproval: BeamlineRequiringApproval): Observable<BeamlineRequiringApproval> {
    return this.http.post<BeamlineRequiringApproval>(this.api, blRequiringApproval);
  }

  UpdateBlApproval(blRequiringApproval: BeamlineRequiringApproval): Observable<BeamlineRequiringApproval> {
    return this.http.put<BeamlineRequiringApproval>(this.api + '/' + blRequiringApproval.id, blRequiringApproval);
  }

  DeleteBlApproval(id: number) {
    return this.http.delete(this.api + '/' + id);
  }

  UpdateActionBlApproval(id: number, action: Action, blRequiringApproval: BeamlineRequiringApproval): Observable<any> {
    return this.http.put<BeamlineRequiringApproval>(this.api + '/' + id + '/' + action, blRequiringApproval);
  }

  //TOCA
  GetActiveTOCAs(): Observable<TOCA[]> {
    return this.http.get<TOCA[]>(this.api + '/TOCA');
  }

  /**
   * Resolves the approved TOCA list for a location, using catalog relations only when BRATOI has no active entry.
   * @param location - Beamline or accelerator location requesting TOCA options.
   * @param bratois - BRATOI entries loaded from the shared store.
   * @param catalogTocas - TOCAs loaded from the catalog store.
   * @returns TOCAs that may be displayed for the location.
   */
  resolveAvailableTocas(location: Resource | Location, bratois: BeamlineRequiringApproval[] = [], catalogTocas: TOCA[] = []): TOCA[] {
    const locationBratois = bratois.filter(b =>
      !!b?.isActive &&
      (b.resourceId === location.id || b.resource?.id === location.id)
    );
    const approvedBratois = locationBratois.filter(b => this.isApprovedAction(b.action));

    // An active BRATOI entry is authoritative while pending, so unapproved TOCAs remain hidden.
    if (locationBratois.length && !approvedBratois.length) {
      return [];
    }

    if (approvedBratois.length) {
      // Approved BRATOI TOCAs can be marked inactive by the approval lifecycle.
      return this.deduplicateTocas(approvedBratois.flatMap(b => b.tocas ?? []));
    }

    const relatedCatalogTocas = catalogTocas.filter(t =>
      t.isActive !== false &&
      (
        t.resourceTOCARelations?.some(r => r.resourceId === location.id || r.resource?.id === location.id) ||
        t.beamlineRequiringApprovalRelations?.some(r =>
          this.isApprovedAction(r.beamlineRequiringApproval?.action) &&
          (r.beamlineRequiringApproval?.resourceId === location.id || r.beamlineRequiringApproval?.resource?.id === location.id)
        )
      )
    );

    return this.deduplicateTocas(relatedCatalogTocas);
  }

  /**
   * Resolves approved locations sharing a TOCA and removes duplicate resources across BRATOI and catalog relations.
   * @param tocaId - Positive TOCA identifier.
   * @param bratois - BRATOI entries loaded from the shared store.
   * @param catalogTocas - TOCAs loaded from the catalog store.
   * @param resources - Resource catalog used to hydrate partial relations.
   * @returns One approved related resource per resource ID.
   */
  resolveRelatedResources(tocaId: number, bratois: BeamlineRequiringApproval[] = [], catalogTocas: TOCA[] = [], resources: Resource[] = []): Resource[] {
    const approvedBratoiResources = bratois
      .filter(b =>
        !!b?.isActive &&
        this.isApprovedAction(b.action) &&
        b.tocas?.some(t => t.id === tocaId)
      )
      .map(b => b.resource ?? resources.find(r => r.id === b.resourceId))
      .filter((r): r is Resource => !!r);

    const matchingTocas = this.deduplicateTocas([
      ...bratois.flatMap(b => b.tocas ?? []).filter(t => t.id === tocaId),
      ...catalogTocas.filter(t => t.id === tocaId)
    ]);

    const approvedRelationResources = matchingTocas
      .flatMap(t => t.beamlineRequiringApprovalRelations ?? [])
      .filter(r =>
        !!r.beamlineRequiringApproval?.isActive &&
        this.isApprovedAction(r.beamlineRequiringApproval?.action)
      )
      .map(r => r.beamlineRequiringApproval?.resource ?? resources.find(resource => resource.id === r.beamlineRequiringApproval?.resourceId))
      .filter((r): r is Resource => !!r);

    const catalogResources = matchingTocas
      .flatMap(t => t.resourceTOCARelations ?? [])
      .map(r => r.resource ?? resources.find(resource => resource.id === r.resourceId))
      .filter((r): r is Resource => !!r)
      // Resource relations exist before BRATOI approval, so active pending entries must not contribute locations.
      .filter(resource => this.canUseCatalogResource(tocaId, resource, bratois));

    return this.deduplicateResources([
      ...approvedBratoiResources,
      ...approvedRelationResources,
      ...catalogResources
    ]);
  }

  /**
   * Removes duplicate resources by ID while preserving discovery order.
   * @param resources - Resources collected from TOCA and BRATOI relations.
   * @returns One resource per ID.
   */
  deduplicateResources(resources: Resource[]): Resource[] {
    return Array.from(new Map(resources.filter(r => !!r?.id).map(r => [r.id, r])).values());
  }

  /**
   * Allows catalog relations only when no active BRATOI entry supersedes them or the entry is approved for the TOCA.
   * @param tocaId - Positive TOCA identifier.
   * @param resource - Related beamline or accelerator location.
   * @param bratois - BRATOI entries loaded from the shared store.
   */
  private canUseCatalogResource(tocaId: number, resource: Resource, bratois: BeamlineRequiringApproval[]): boolean {
    const resourceBratois = bratois.filter(b =>
      !!b?.isActive &&
      (b.resourceId === resource.id || b.resource?.id === resource.id)
    );

    return !resourceBratois.length || resourceBratois.some(b =>
      this.isApprovedAction(b.action) &&
      b.tocas?.some(t => t.id === tocaId)
    );
  }

  /**
   * Normalizes the action representations returned by existing BRATOI endpoints.
   * @param action - Enum, numeric string, or display label to evaluate.
   */
  private isApprovedAction(action: any): boolean {
    return action === Action.Approve || action === `${Action.Approve}` || action === 'Approve' || action === 'Approved';
  }

  /**
   * Removes duplicate TOCAs by ID while preserving discovery order.
   * @param tocas - TOCAs collected from BRATOI and catalog relations.
   */
  deduplicateTocas(tocas: TOCA[]): TOCA[] {
    return Array.from(new Map(tocas.filter(t => !!t?.id).map(t => [t.id, t])).values());
  }

  /**
   * Returns mention IDs for TOCAs belonging to active, unapproved BRATOI rows.
   * @param bratois - BRATOI entries loaded from the shared store.
   * @returns Negative TOCA IDs matching the work-description mention format.
   */
  getUnapprovedTocaMentionIds(bratois: BeamlineRequiringApproval[] = []): number[] {
    const unapprovedTocas = bratois
      .filter(b =>
        !!b?.isActive &&
        !this.isApprovedAction(b.action)
      )
      .flatMap(b => b.tocas ?? []);

    return this.deduplicateTocas(unapprovedTocas).map(t => -t.id);
  }
}
